/**
 * EditorialAiAssistant — shared AI suggestion button + preview + optional advanced controls.
 * Domain-agnostic shell: callers provide task, context builder, and onAccept callback.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { AiSuggestionPreview } from "@/components/admin/AiSuggestionPreview";
import {
  useEditorialAi,
  type EditorialAiTask,
  type EditorialAiContext,
  type EditorialAiOptions,
  type EditorialAiAction,
  type EditorialAiLength,
  type EditorialAiLengthProfile,
} from "@/hooks/useEditorialAi";
import { cn } from "@/lib/utils";

// ─── Props ──────────────────────────────────────

interface EditorialAiAssistantProps {
  /** AI task to invoke */
  task: EditorialAiTask;
  /** Builds context payload at invocation time (called on click) */
  buildContext: () => EditorialAiContext;
  /** Called when editor accepts the suggestion */
  onAccept: (text: string) => void;
  /** Disable the button */
  disabled?: boolean;
  /** Show advanced controls panel */
  showAdvanced?: boolean;
  /** Override default button label */
  buttonLabel?: string;
  /** Override default loading label */
  loadingLabel?: string;
  /** Allow switching between generate/rewrite/shorten if true */
  allowRewrite?: boolean;
  /** Current field has existing text (enables rewrite/shorten options) */
  hasExistingText?: boolean;
  /** Rewrite task to use instead of default task */
  rewriteTask?: EditorialAiTask;
  /** Additional context fields for rewrite mode */
  rewriteContextOverride?: Partial<EditorialAiContext>;
  /** Length profile for field-specific length guidance (summary vs body) */
  lengthProfile?: EditorialAiLengthProfile;
  /** Current text to show in preview for comparison */
  currentText?: string;
}

// ─── Length labels ───────────────────────────────

const LENGTH_OPTIONS: { value: EditorialAiLength; label: string }[] = [
  { value: "short", label: "Kort" },
  { value: "medium", label: "Lagom" },
  { value: "long", label: "Längre" },
];

// ─── Action labels ──────────────────────────────

interface ActionOption {
  value: EditorialAiAction;
  label: string;
  requiresText?: boolean;
}

const ALL_ACTIONS: ActionOption[] = [
  { value: "generate", label: "Nytt förslag" },
  { value: "rewrite", label: "Förbättra befintlig", requiresText: true },
  { value: "shorten", label: "Korta ned text", requiresText: true },
];

// ─── Component ──────────────────────────────────

export function EditorialAiAssistant({
  task,
  buildContext,
  onAccept,
  disabled = false,
  showAdvanced = false,
  buttonLabel = "Få ett förslag",
  loadingLabel = "Assistenten funderar…",
  allowRewrite = false,
  hasExistingText = false,
  rewriteTask = "rewrite_in_farmly_tone",
  rewriteContextOverride,
  lengthProfile,
  currentText,
}: EditorialAiAssistantProps) {
  const { generate, loading } = useEditorialAi();
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedLength, setSelectedLength] = useState<EditorialAiLength>("medium");
  const [selectedAction, setSelectedAction] = useState<EditorialAiAction>("generate");

  const effectiveAction: EditorialAiAction =
    allowRewrite && hasExistingText && (selectedAction === "rewrite" || selectedAction === "shorten")
      ? selectedAction
      : "generate";

  const handleGenerate = async () => {
    setSuggestion(null);
    const baseContext = buildContext();

    let finalTask = task;
    let finalContext = baseContext;

    if (effectiveAction === "rewrite" && hasExistingText) {
      finalTask = rewriteTask;
      finalContext = {
        ...baseContext,
        text: baseContext.currentText,
        ...rewriteContextOverride,
      };
    } else if (effectiveAction === "shorten" && hasExistingText) {
      // Shorten uses the current text; task stays the same for routing
      finalContext = {
        ...baseContext,
        text: baseContext.currentText,
      };
    }

    const options: EditorialAiOptions = {};
    if (showAdvanced) {
      options.length = selectedLength;
      options.action = effectiveAction;
      if (lengthProfile) options.lengthProfile = lengthProfile;
    }

    const result = await generate(finalTask, finalContext, showAdvanced ? options : undefined);
    if (result) setSuggestion(result);
  };

  const handleAccept = () => {
    if (suggestion) {
      onAccept(suggestion);
      setSuggestion(null);
    }
  };

  const handleDismiss = () => {
    setSuggestion(null);
  };

  const availableActions = ALL_ACTIONS.filter(
    (a) => !a.requiresText || hasExistingText
  );

  return (
    <div className="space-y-2">
      {/* Action row: button + optional advanced toggle */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs gap-1.5 text-muted-foreground hover:text-foreground"
          disabled={disabled || loading}
          onClick={handleGenerate}
        >
          {loading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            <>
              <Sparkles className="h-3 w-3" />
              {buttonLabel}
            </>
          )}
        </Button>

        {showAdvanced && !loading && (
          <button
            type="button"
            onClick={() => setAdvancedOpen((o) => !o)}
            className={cn(
              "inline-flex items-center gap-1 text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors select-none",
              advancedOpen && "text-muted-foreground"
            )}
          >
            <ChevronDown className={cn("h-3 w-3 transition-transform duration-150", advancedOpen && "rotate-180")} />
            {advancedOpen ? "Dölj" : "Fler val"}
          </button>
        )}
      </div>

      {/* Advanced controls */}
      {showAdvanced && (
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleContent>
            <div className="rounded-md border border-border/40 bg-muted/15 px-3 py-2.5 space-y-2.5 mt-1 animate-in fade-in-0 slide-in-from-top-1 duration-150">
              {/* Length selector */}
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground/80 uppercase tracking-wide">Längd</p>
                <div className="flex gap-1">
                  {LENGTH_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSelectedLength(opt.value)}
                      className={cn(
                        "px-2 py-0.5 rounded text-[11px] transition-colors",
                        selectedLength === opt.value
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted/40 text-muted-foreground hover:bg-muted"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action selector */}
              {allowRewrite && availableActions.length > 1 && (
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-muted-foreground/80 uppercase tracking-wide">Typ av hjälp</p>
                  <div className="flex flex-wrap gap-1">
                    {availableActions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSelectedAction(opt.value)}
                        className={cn(
                          "px-2 py-0.5 rounded text-[11px] transition-colors",
                          selectedAction === opt.value
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted/40 text-muted-foreground hover:bg-muted"
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Suggestion preview */}
      {suggestion && (
        <AiSuggestionPreview
          suggestion={suggestion}
          currentText={currentText}
          onAccept={handleAccept}
          onDismiss={handleDismiss}
        />
      )}
    </div>
  );
}
